/**
 * QuotaWarningBanner - Floating banner that shows API quota usage
 *
 * Appears automatically when quota usage exceeds thresholds:
 * - 60%: Warning (amber)
 * - 80%: Critical (red)
 * - 100%: Exceeded (red pulsing)
 *
 * Position: Fixed bottom-right corner
 */

import { useState, useEffect, useCallback } from 'react';
import { useApiHealth } from '../../contexts/ApiHealthContext';
import {
  FiAlertTriangle as AlertTriangle,
  FiX as X,
  FiActivity as Activity,
} from 'react-icons/fi';

const QuotaWarningBanner = () => {
  const {
    rateLimits,
    getQuotaPercentage,
    QUOTA_WARNING_THRESHOLD,
    QUOTA_CRITICAL_THRESHOLD,
  } = useApiHealth();

  const [isDismissed, setIsDismissed] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number | null>(null);

  // Get quota info for genaiops (primary service)
  const limits = rateLimits.genaiops;
  const percentUsed = getQuotaPercentage('genaiops');

  // Calculate reset countdown
  useEffect(() => {
    if (limits.resetMinute) {
      const updateCountdown = () => {
        const now = Math.floor(Date.now() / 1000);
        const remaining = Math.max(0, limits.resetMinute! - now);
        setCountdown(remaining);
      };

      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [limits.resetMinute]);

  // Auto-show banner again if quota worsens
  useEffect(() => {
    if (isDismissed && percentUsed !== null) {
      // If quota jumps to critical or exceeded after being dismissed, show again
      if (percentUsed >= QUOTA_CRITICAL_THRESHOLD) {
        setIsDismissed(false);
      }
    }
  }, [percentUsed, isDismissed, QUOTA_CRITICAL_THRESHOLD]);

  // Reset dismissed state when quota resets
  useEffect(() => {
    if (percentUsed != null && percentUsed < QUOTA_WARNING_THRESHOLD) {
      setIsDismissed(false);
    }
  }, [percentUsed, QUOTA_WARNING_THRESHOLD]);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
  }, []);

  // Don't show if:
  // - No quota data yet
  // - Below warning threshold
  // - User dismissed (unless critical)
  if (
    percentUsed === null ||
    percentUsed < QUOTA_WARNING_THRESHOLD ||
    (isDismissed && percentUsed < QUOTA_CRITICAL_THRESHOLD)
  ) {
    return null;
  }

  // Determine visual state
  const isExceeded = percentUsed >= 100;
  const isCritical = percentUsed >= QUOTA_CRITICAL_THRESHOLD;

  // Styling based on state
  let bgColor: string;
  if (isExceeded) {
    bgColor = 'bg-red-600';
  } else if (isCritical) {
    bgColor = 'bg-red-500';
  } else {
    bgColor = 'bg-amber-500';
  }

  let borderColor: string;
  if (isExceeded) {
    borderColor = 'border-red-700';
  } else if (isCritical) {
    borderColor = 'border-red-600';
  } else {
    borderColor = 'border-amber-600';
  }

  const pulseClass = isExceeded ? 'animate-pulse' : '';

  // Message based on state
  const getMessage = (): string => {
    if (isExceeded) {
      return 'API limit reached!';
    }
    if (isCritical) {
      return 'API limit almost reached';
    }
    return 'Approaching API limit';
  };

  // Format countdown
  const formatCountdown = (seconds: number | null): string => {
    if (seconds == null) return '';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 max-w-sm ${pulseClass}`}
      role="alert"
      aria-live="polite"
    >
      <div
        className={`${bgColor} ${borderColor} border rounded-lg shadow-lg text-white p-4`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            {isExceeded ? (
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <Activity className="w-5 h-5 flex-shrink-0" />
            )}
            <span className="font-semibold">{getMessage()}</span>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress bar */}
        <div className="mt-3">
          <div className="flex justify-between text-sm mb-1">
            <span>{percentUsed}% used</span>
            <span>
              {limits.remainingMinute === null
                ? ''
                : `${limits.remainingMinute} left`}
            </span>
          </div>
          <div className="w-full bg-white/30 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${
                isExceeded || isCritical ? 'bg-white' : 'bg-white/80'
              }`}
              style={{ width: `${Math.min(percentUsed, 100)}%` }}
            />
          </div>
        </div>

        {/* Reset countdown */}
        {countdown !== null && (
          <div className="mt-2 text-sm opacity-90">
            Resets in {formatCountdown(countdown)}
          </div>
        )}

        {/* Help text for exceeded state */}
        {isExceeded && (
          <div className="mt-2 text-sm opacity-90">
            Requests will be retried automatically when quota resets.
          </div>
        )}
      </div>
    </div>
  );
};

export default QuotaWarningBanner;
